import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Heart, MessageCircle, Flame, Radio, Lock, Crown, Rocket } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROBOTS = ["/robots/robot_a.png", "/robots/robot_b.png", "/robots/hero_robot.png"];
const robotFor = (anon = "") => ROBOTS[(anon.charCodeAt(0) + (anon.charCodeAt(anon.length - 1) || 0)) % ROBOTS.length];

const timeAgo = (iso, t) => {
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return t.now;
  if (m < 60) return `${m}${t.mAgo}`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}${t.hAgo}`;
  return `${Math.floor(h / 24)}${t.dAgo}`;
};

export const PublicWall = ({ t }) => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/wall/public`).then(({ data }) => setPosts(data || [])).catch(() => setPosts([]));
  }, []);

  if (!posts.length) return null;

  return (
    <section id="live-wall" className="relative max-w-7xl mx-auto px-6 md:px-12 mt-8 pb-6" data-testid="public-wall-section">
      <div className="text-center max-w-3xl mx-auto mb-12">
        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#00FFAA] border border-[#00FFAA]/30 bg-[#00FFAA]/5 px-4 py-2 rounded-full mb-6" data-testid="public-wall-badge">
          <span className="relative flex w-2 h-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00FFAA] opacity-75" />
            <span className="relative inline-flex rounded-full w-2 h-2 bg-[#00FFAA]" />
          </span>
          {t.badge}
        </span>
        <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight text-white" data-testid="public-wall-title">
          {t.title}{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00FFAA] to-[#00F5FF]">{t.highlight}</span>
        </h2>
        <p className="text-base md:text-lg font-light text-[#A0A0B0] mt-4 leading-relaxed">{t.sub}</p>
      </div>

      {/* Feed grid with bottom fade lock */}
      <div className="relative">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="public-wall-grid">
          {posts.slice(0, 6).map((p, i) => (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="relative bg-[#0F0F1A]/80 backdrop-blur-xl border border-white/10 rounded-3xl p-5 overflow-hidden hover:border-[#00FFAA]/30 transition-colors"
              data-testid={`public-post-${i}`}
            >
              <div className="flex items-center gap-2.5 mb-3">
                <img
                  src={p.avatar_img || robotFor(p.anon)}
                  alt={p.avatar_name}
                  loading="lazy"
                  className="w-9 h-9 rounded-full object-contain p-0.5 bg-[#B026FF]/15 border shrink-0"
                  style={p.author_tier === 4
                    ? { borderColor: "#FFD700", boxShadow: "0 0 14px rgba(255,215,0,0.6)" }
                    : { borderColor: "rgba(176,38,255,0.4)" }}
                />
                <div className="min-w-0">
                  <p className="font-heading font-black text-xs text-white flex items-center gap-1.5">
                    {p.avatar_name}
                    {p.author_tier === 4 && <Crown size={11} className="text-[#FFD700] fill-[#FFD700]/40" />}
                  </p>
                  <p className="text-[9px] text-white/30">{p.venue ? `📍 ${p.venue} · ` : ""}{timeAgo(p.created_at, t)}</p>
                </div>
              </div>
              <p className="text-xs text-white/80 leading-relaxed mb-4 line-clamp-3">{p.content}</p>

              {p.comments?.length > 0 && (
                <div className="space-y-2 mb-4">
                  {p.comments.map((c, ci) => (
                    <div key={ci} className="flex items-start gap-2">
                      <img src={c.avatar_img || robotFor(c.anon)} alt={c.avatar_name} className="w-5 h-5 rounded-full object-contain p-0.5 bg-[#00F5FF]/15 border border-[#00F5FF]/30 shrink-0 mt-0.5" />
                      <p className="text-[10px] text-white/50 leading-snug min-w-0">
                        <span className="font-bold" style={{ color: c.author_tier === 4 ? "#FFD700" : "#00F5FF" }}>{c.avatar_name}</span>{" "}
                        {c.text}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2 border-t border-white/5 pt-3">
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#FF6EC7]"><Heart size={11} className="fill-[#FF6EC7]/50" /> {p.likes}</span>
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#00F5FF]"><MessageCircle size={11} /> {p.total_comments}</span>
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#FFA726] ml-auto"><Flame size={11} className="fill-[#FFA726]/40" /> {p.popularity}</span>
              </div>
            </motion.article>
          ))}
        </div>

        {/* fade-out + lock CTA */}
        <div className="absolute inset-x-0 -bottom-2 h-40 bg-gradient-to-t from-[#05050A] via-[#05050A]/80 to-transparent pointer-events-none" />
      </div>

      <div className="relative flex flex-col items-center -mt-10 z-10">
        <p className="inline-flex items-center gap-2 text-[11px] text-white/50 mb-4" data-testid="public-wall-lock-note">
          <Lock size={12} className="text-[#00FFAA]" /> {t.lockNote}
        </p>
        <button
          data-testid="public-wall-cta"
          onClick={() => navigate("/register")}
          className="inline-flex items-center justify-center gap-2 px-9 py-4 text-sm font-bold uppercase tracking-widest text-[#05050A] transition-all duration-300 bg-gradient-to-r from-[#00FFAA] to-[#00F5FF] rounded-full shadow-[0_0_28px_rgba(0,255,170,0.5)] hover:shadow-[0_0_45px_rgba(0,255,170,0.8)] hover:-translate-y-1"
        >
          <Rocket size={16} />
          {t.join}
        </button>
      </div>

      <div className="relative max-w-7xl mx-auto mt-16" aria-hidden="true">
        <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-[#00FFAA] to-transparent shadow-[0_0_18px_rgba(0,255,170,0.7)]" />
      </div>

      {/* live radio icon decoration */}
      <Radio className="hidden" size={1} />
    </section>
  );
};
